import { Card, CardContent, Typography, Box, useTheme } from '@mui/material'
import { PieChart } from '@mui/x-charts/PieChart'
import { useTranslation } from 'react-i18next'

interface PieChartCardProps {
  title: string
  data: Array<{ label: string; value: number; color?: string }>
  size?: 'small' | 'medium'
  variant?: 'card' | 'plain'
}

const PieChartCard = ({ title, data, size = 'small', variant = 'card' }: PieChartCardProps) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['common'])

  const chartSize = size === 'small' ? 150 : 210
  const cardHeight = size === 'small' ? 200 : 250

  // Use theme colors when color not provided
  const themeColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.info.main,
  ]

  const chartData = data.map((item, index) => ({
    id: index,
    value: item.value,
    label: item.label,
    color: item.color || themeColors[index % themeColors.length],
  }))

  const hasData = data.length > 0 && data.some(d => d.value > 0)

  const content = (
    <>
      <Typography variant="subtitle2" gutterBottom={false} sx={{ fontWeight: 600, textAlign: 'center', mb: -10 }}>
        {title}
      </Typography>
      {hasData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <PieChart
            series={[{
              data: chartData,
              highlightScope: { faded: 'global', highlighted: 'item' },
              faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
            }]}
            width={chartSize}
            height={chartSize}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'bottom', horizontal: 'middle' },
                padding: 0,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 4,
                itemGap: 10,
                labelStyle: {
                  fontSize: 11,
                },
              },
            }}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {translate('no-data', { ns: 'common', defaultValue: 'No data available' })}
          </Typography>
        </Box>
      )}
    </>
  )

  if (variant === 'plain') {
    return (
      <Box
        sx={{
          minHeight: cardHeight,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
        }}
      >
        {content}
      </Box>
    )
  }

  return (
    <Card sx={{ height: cardHeight, display: 'flex', flexDirection: 'column' }}>
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0.5,
          pb: -2
        }}
      >
        {content}
      </CardContent>
    </Card>
  )
}

export default PieChartCard
